import { ChannelLockType, GrantStatus } from '@constants';

// Re-exported so the existing barrel (`@components/communication-channels`)
// stays a valid import path. Canonical definitions live in `@constants`.
export { CHANNEL_ORDER, CHANNEL_ICONS } from '@constants';

export const ChannelMode = {
  NOTICE: 'showInNotice', // initial collection
  EUP: 'showInEUP', // preference center
};

// SINGLE SOURCE OF TRUTH: can the user change this channel's grant state?
// Notice: every mandatory CP is fixed.
// EUP:    mandatory CPs are fixed UNLESS withdrawable-after-collect.
export const isCpEditable = (cp, mode = ChannelMode.NOTICE) => {
  if (!cp) return false;
  if (!cp.mandatory) return true;
  return mode === ChannelMode.EUP ? !!cp.withdrawableAfterCollect : false;
};

// A channel is "locked" (shows the lock badge) when it's mandatory AND not
// editable. Derived from isCpEditable so the badge can never disagree with
// whether the chip actually toggles.
export const isCpLocked = (cp, mode = ChannelMode.NOTICE) => (
  !!cp?.mandatory && !isCpEditable(cp, mode)
);

// Cross-purpose rollup:
//   - selected = any contributing purpose has this channel ENABLED (locked-on or user-enabled)
//   - lock:
//     - all contributing locked → AMBER (brand)
//     - some (not all) locked  → GRAY (partial)
//     - else                   → null
export const computeRollup = (channelId, profilePurposes, visibleField = 'showInNotice') => {
  const contributing = (profilePurposes || [])
    .map((purpose) => {
      const cp = (purpose?.communicationPreferences || [])
        .find((c) => c.channel === channelId && c[visibleField]);
      if (!cp) return null;
      return { cp, purpose };
    })
    .filter(Boolean);

  if (contributing.length === 0) return { selected: false, lock: null };

  const someEnabled = contributing.some(({ cp, purpose }) => (
    isCpLocked(cp, visibleField) || purpose?.channelState?.[channelId] === GrantStatus.GRANTED
  ));
  const allLocked = contributing.every(({ cp }) => isCpLocked(cp, visibleField));
  const someLocked = contributing.some(({ cp }) => isCpLocked(cp, visibleField));

  let lock = null;
  if (allLocked) lock = ChannelLockType.AMBER;
  else if (someLocked) lock = ChannelLockType.GRAY;

  return { selected: someEnabled, lock };
};
